import { getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import { CustomerCardDetailVm, CustomerCardProfilesVm, CustomerDetailVm, EnumsDto } from '@/api';
import { AccountTypes } from '@/constants/AccountTypes';
import { MIN_CUSTOMER_AGE } from '@/config/config';
import { toDate } from '@/utils/dateTime';
import { differenceInYears, startOfDay } from 'date-fns';

@Module({ dynamic: true, store, namespaced: true, name: 'userAccount' })
class UserAccount extends VuexModule {
  public hasActiveOrProcessedCardApplication = false;
  public cardDetailData = {
    state: {},
    customer: {},
    electronicWallet: {},
    profileOne: {} as CustomerCardProfilesVm,
  } as unknown as CustomerCardDetailVm;
  public selectedCustomerData = {} as CustomerDetailVm;
  public selectedAccount = AccountTypes.REGISTERED;
  public canVirtualCardApplicationBeCreated = true;

  @Mutation
  public setCardData(payload: CustomerCardDetailVm): void {
    this.cardDetailData = payload;
  }

  @Mutation
  public setSelectedCustomerData(payload: CustomerDetailVm): void {
    this.selectedCustomerData = payload;
  }

  @Mutation
  public setCardApplicationActiveOrProcessed(activeOrProcessed: boolean) {
    this.hasActiveOrProcessedCardApplication = activeOrProcessed;
  }

  @Mutation
  public setSelectedAccount(accountType: string) {
    this.selectedAccount = accountType;
  }

  @Mutation
  public clearSelectedCustomerData(): void {
    this.selectedCustomerData = {} as CustomerDetailVm;
  }

  @Mutation
  public setVirtualCardApplicationCanBeCreated(canBeCreated: boolean): void {
    this.canVirtualCardApplicationBeCreated = canBeCreated;
  }

  @Mutation
  public clearCardData(): void {
    this.cardDetailData = {
      state: {},
      customer: {},
      electronicWallet: {},
    } as CustomerCardDetailVm;
  }

  public get isCardActive(): boolean {
    return this.cardDetailData?.state.name === EnumsDto.cardStates.ACTIVE;
  }

  public get isCardTypeMobilePhone(): boolean {
    return this.cardDetailData?.type?.name === EnumsDto.cardTypes.MOBILE_PHONE;
  }

  public get isCardTypeBank(): boolean {
    return this.cardDetailData?.type?.name === EnumsDto.cardTypes.BANK_CARD;
  }

  public get isCardTypeWithoutStructure(): boolean {
    return this.cardDetailData?.type?.name === EnumsDto.cardTypes.SMART_CARD_WITHOUT_STRUCTURE;
  }

  public get isCardTypeWithChip(): boolean {
    return this.cardDetailData?.customerCardType === CustomerCardDetailVm.customerCardType.PERSONAL;
  }

  public get isNonChipCard(): boolean {
    const name = this.cardDetailData?.type?.name;
    return !!(
      name &&
      (
        [
          EnumsDto.cardTypes.BANK_CARD,
          EnumsDto.cardTypes.MOBILE_PHONE,
          EnumsDto.cardTypes.WHITE_LIST_CARD,
          EnumsDto.cardTypes.EXTERNAL_CARD,
        ] as string[]
      ).includes(name)
    );
  }

  public get cardNumber(): string | undefined {
    return this.cardDetailData?.cardNumber;
  }

  public get hasCardDetailData(): boolean {
    return !!this.cardDetailData?.id;
  }

  public get personalDataVerified(): boolean {
    return Boolean(
      this.cardDetailData?.customer.personalDataVerifiedDate &&
        this.cardDetailData?.customer.personalDataVerifiedBy?.id,
    );
  }

  public get isSelectedAccountRegistered() {
    return this.selectedAccount === AccountTypes.REGISTERED;
  }

  public get customerIsChild() {
    const currentDate = toDate(new Date());
    const customerYears = differenceInYears(startOfDay(currentDate), startOfDay(this.selectedCustomerData.birthDay));

    return customerYears < MIN_CUSTOMER_AGE;
  }
}

export default getModule(UserAccount);
